import React from 'react'
import { Button, Form, InputNumber, Modal, ModalProps, Select, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { Company } from '../company'
import { useUpdateCompanyAgencySettings } from '../hooks'
import { FormItem } from '../../components/form-item'
import { addValidationOnError } from '../../util'

type AgencySettingsModalProps = {
    company: Company
} & Pick<ModalProps, 'open' | 'onCancel'>

type AgencySettingsForm = {
    isAgency: boolean
    period: 'monthly' | 'yearly'
    baseAmount: number | null
    overageAmount: number | null
    conversationLimit: number | null
    userLimit: number | null
    siteLimit: number | null
    messageLimit: number | null
}

export function AgencySettingsModal({ company, open, onCancel }: AgencySettingsModalProps) {
    const { t } = useTranslation()
    const { mutate, isPending } = useUpdateCompanyAgencySettings(company.id)
    const [form] = Form.useForm<AgencySettingsForm>()
    const isAgency = Form.useWatch('isAgency', form) ?? company.isAgency

    const toCents = (amount: number | null) => (amount === null ? null : Math.round(amount * 100))

    const handleSave = (values: AgencySettingsForm) => {
        mutate(
            {
                isAgency: values.isAgency,
                period: values.isAgency ? values.period : null,
                baseUnitAmount: values.isAgency ? toCents(values.baseAmount) : null,
                overageUnitAmount: values.isAgency ? toCents(values.overageAmount) : null,
                conversationLimit: values.isAgency ? values.conversationLimit : null,
                userLimit: values.isAgency ? values.userLimit : null,
                siteLimit: values.isAgency ? values.siteLimit : null,
                messageLimit: values.isAgency ? values.messageLimit : null,
            },
            {
                onSuccess: () => onCancel?.({} as never),
                onError: addValidationOnError(form),
            }
        )
    }

    return (
        <Modal
            title={t('Agency tier')}
            open={open}
            onCancel={onCancel}
            footer={[
                <Button
                    key="cancel"
                    onClick={onCancel}
                >
                    {t('Cancel')}
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={isPending}
                    onClick={() => form.submit()}
                >
                    {t('Save')}
                </Button>,
            ]}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
                initialValues={{
                    isAgency: company.isAgency,
                    period: company.agencyPeriod === 'yearly' ? 'yearly' : 'monthly',
                    baseAmount: company.agencyBaseUnitAmount == null ? null : company.agencyBaseUnitAmount / 100,
                    overageAmount:
                        company.agencyOverageUnitAmount == null ? null : company.agencyOverageUnitAmount / 100,
                    conversationLimit: company.conversationLimit ?? null,
                    userLimit: company.userLimit ?? null,
                    siteLimit: company.siteLimit ?? null,
                    messageLimit: company.messageLimit ?? null,
                }}
                onFinish={handleSave}
            >
                <FormItem
                    label={t('Agency tier enabled')}
                    name="isAgency"
                    valuePropName="checked"
                >
                    <Switch />
                </FormItem>

                <FormItem
                    label={t('Billing period')}
                    name="period"
                >
                    <Select
                        disabled={!isAgency}
                        options={[
                            { label: t('period.monthly'), value: 'monthly' },
                            { label: t('period.yearly'), value: 'yearly' },
                        ]}
                    />
                </FormItem>

                <FormItem
                    label={t('Base price')}
                    name="baseAmount"
                >
                    <InputNumber
                        className="w-full"
                        min={0.01}
                        max={1000000}
                        step={0.01}
                        precision={2}
                        prefix="€"
                        disabled={!isAgency}
                    />
                </FormItem>

                <FormItem
                    label={t('Overage price')}
                    name="overageAmount"
                >
                    <InputNumber
                        className="w-full"
                        min={0.01}
                        max={1000000}
                        step={0.01}
                        precision={2}
                        prefix="€"
                        disabled={!isAgency}
                    />
                </FormItem>

                <FormItem
                    label={t('Conversations per month')}
                    name="conversationLimit"
                >
                    <InputNumber
                        className="w-full"
                        min={1}
                        max={1000000}
                        disabled={!isAgency}
                        placeholder={t('Unlimited')}
                    />
                </FormItem>

                <FormItem
                    label={t('Users')}
                    name="userLimit"
                >
                    <InputNumber
                        className="w-full"
                        min={1}
                        max={1000000}
                        disabled={!isAgency}
                        placeholder={t('Unlimited')}
                    />
                </FormItem>

                <FormItem
                    label={t('Websites')}
                    name="siteLimit"
                >
                    <InputNumber
                        className="w-full"
                        min={1}
                        max={1000000}
                        disabled={!isAgency}
                        placeholder={t('Unlimited')}
                    />
                </FormItem>

                <FormItem
                    label={t('Website messages per month')}
                    name="messageLimit"
                >
                    <InputNumber
                        className="w-full"
                        min={1}
                        max={100000000}
                        disabled={!isAgency}
                        placeholder={t('Unlimited')}
                    />
                </FormItem>
            </Form>
        </Modal>
    )
}
